/**
 * Photo models - converted from remote.editor.photo classes
 */

/**
 * PhotoInfo - from remote.editor.photo.PhotoInfo
 * Represents an uploaded photo in the system
 */
export interface PhotoInfo {
  id: number | string;
  serverId: number | string;
  serverName: string;
  pathStructureId: number | string;
  md5: string;
  name?: string;
  width?: number;
  height?: number;
  sizes?: PhotoSize[];
  fileTypeId?: number;
  type?: number;
  size?: string;
  provider?: number;
  importDate?: string;
  creationDate?: string | null;
  url?: string;
  thumbnail?: string;
  /** Backend-rendered PNG preview of a vector/template upload (AI/PDF/EPS),
   *  returned by the upload endpoint. Used as the preview instead of a
   *  client-side conversion. */
  urlPNG?: string;
}

/**
 * PhotoSize - from remote.editor.photo.PhotoSize
 * Represents a size variant of a photo
 */
export interface PhotoSize {
  width: number;
  height: number;
  name: string;
  url?: string;
}

/**
 * PhotoUpload - tracks a file being uploaded
 */
export interface PhotoUpload {
  id: string;               // Unique upload ID
  file: File;               // The file to upload
  md5: string;              // MD5 hash of the file
  name: string;             // Original filename
  /** Top-level folder the file came from (folder drag-drop / folder picker).
   *  Drives the dynamic folder filter chips on the product-files grid;
   *  persisted in the draft so the chips survive a reload. */
  sourceFolder?: string;
  status: PhotoUploadStatus;
  progress: number;         // 0-100
  photoInfo?: PhotoInfo;    // Server response after upload
  error?: string;
  /**
   * Set by the draft-restore server verification: the file was restored from
   * a saved draft but its photo no longer exists on the server (deleted
   * after the draft was saved). Distinct from an ordinary upload ERROR —
   * this flag hard-blocks the cart until the file is replaced/removed.
   */
  serverMissing?: boolean;
  /**
   * Mid-size server variant URL carried through draft restore→resave cycles.
   * Fresh uploads derive it from photoInfo.sizes[]; restored uploads have no
   * sizes[] anymore, so the previously persisted value survives here.
   */
  previewUrl?: string;
  projectId?: number;
  allSizes?: boolean;
  fileTypeId?: number;
  photoType?: number;       // 0=Photo, 1=Background, 2=Clipart
  thumbDataUrl?: string;    // Base64 thumbnail for preview
  bytesTotal?: number;
  bytesLoaded?: number;
  qty: number;              // Quantity of copies for this photo
  
  // Image metadata
  metadata?: {
    width?: number;
    height?: number;
    dpi?: number | null;
    colorProfile?: string | null;
    fileSize?: number;
    sizeValid?: boolean | null;     // true/false = compared; null = indeterminate (no format/no image dims/fetch failed); undefined = not yet attempted
  };
}

export enum PhotoUploadStatus {
  PENDING = 'pending',
  CHECKING = 'checking',    // Checking if photo exists on server
  UPLOADING = 'uploading',
  COMPLETE = 'complete',
  ERROR = 'error',
  CANCELLED = 'cancelled'
}

/**
 * PhotoImportType - from remote.editor.photo.PhotoImportType
 */
export enum PhotoImportType {
  PHOTO = 0,
  BACKGROUND = 1,
  CLIPART = 2
}

/**
 * File type IDs for different file formats
 */
export enum FileTypeId {
  UNKNOWN = 0,
  JPEG = 1,
  PNG = 2,
  TIFF = 3,
  PDF = 4,
  AI = 5,
  PSD = 6
}

/** Maximum allowed upload file size: 85 MB */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 85 * 1024 * 1024;

/** Filter out files exceeding the max upload size. Returns valid files and names of rejected files. */
export function filterOversizedFiles(files: File[]): { validFiles: File[]; oversizedNames: string[] } {
  const validFiles: File[] = [];
  const oversizedNames: string[] = [];
  for (const f of files) {
    if (f.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      oversizedNames.push(f.name);
    } else {
      validFiles.push(f);
    }
  }
  return { validFiles, oversizedNames };
}

/**
 * Get file type ID from file extension
 */
export function getFileTypeId(filename: string): FileTypeId {
  const ext = filename.toLowerCase().split('.').pop();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return FileTypeId.JPEG;
    case 'png':
      return FileTypeId.PNG;
    case 'tif':
    case 'tiff':
      return FileTypeId.TIFF;
    case 'pdf':
      return FileTypeId.PDF;
    case 'ai':
      return FileTypeId.AI;
    case 'psd':
      return FileTypeId.PSD;
    default:
      return FileTypeId.UNKNOWN;
  }
}

/**
 * FilesTypeId - defines which file types are accepted for a finish
 * 0: all file types accepted
 * 1: only jpg,jpeg,png,psd (images)
 * 2: only ai,pdf,eps (vector)
 * 3: only pdf
 */
export enum FilesTypeId {
  ALL = 0,
  IMAGES = 1,
  VECTOR = 2,
  PDF_ONLY = 3,
  /** Frontend-only (the backend sends 0–3): plain photo uploads — jpg/jpeg/png.
   *  Used as the default gate for finish-option file uploads whose backend
   *  filesTypeId is ALL/0 ("Todos os formatos"). */
  PHOTOS = 4
}

/**
 * Get the accept string for file input based on filesTypeId
 */
export function getAcceptStringFromFilesTypeId(filesTypeId: number): string {
  switch (filesTypeId) {
    case FilesTypeId.IMAGES:
      return '.jpg,.jpeg,.png,.psd';
    case FilesTypeId.VECTOR:
      return '.ai,.pdf,.eps';
    case FilesTypeId.PDF_ONLY:
      return '.pdf';
    case FilesTypeId.PHOTOS:
      return '.jpg,.jpeg,.png';
    case FilesTypeId.ALL:
    default:
      return 'image/*,.pdf,.ai,.psd,.tif,.tiff,.eps';
  }
}

/**
 * Get a human-readable description of accepted file types
 */
export function getFilesTypeDescription(filesTypeId: number, allFormatsDescription: string): string {
  switch (filesTypeId) {
    case FilesTypeId.IMAGES:
      return 'JPG, PNG, PSD';
    case FilesTypeId.VECTOR:
      return 'AI, PDF, EPS';
    case FilesTypeId.PDF_ONLY:
      return 'PDF';
    case FilesTypeId.PHOTOS:
      return 'JPG, JPEG, PNG';
    case FilesTypeId.ALL:
    default:
      return allFormatsDescription;
  }
}

/**
 * Check if a file extension is valid for a given filesTypeId
 */
export function isFileTypeValidForFilesTypeId(filename: string, filesTypeId: number): boolean {
  const ext = filename.toLowerCase().split('.').pop();
  if (!ext) return false;
  
  switch (filesTypeId) {
    case FilesTypeId.IMAGES:
      return ['jpg', 'jpeg', 'png', 'psd'].includes(ext);
    case FilesTypeId.VECTOR:
      return ['ai', 'pdf', 'eps'].includes(ext);
    case FilesTypeId.PDF_ONLY:
      return ext === 'pdf';
    case FilesTypeId.PHOTOS:
      return ['jpg', 'jpeg', 'png'].includes(ext);
    case FilesTypeId.ALL:
    default:
      return true;
  }
}

/**
 * Photo imports inside the editors (album editor, gallery editor, storytelling,
 * re-upload) are restricted to JPEG/JPG/PNG only — no PSD/TIFF/PDF/etc. The HTML
 * `accept` attribute is bypassable ("All files" option in the OS dialog), so
 * callers MUST also run `filterEditorImageFiles` on the selected files and
 * reject the rest.
 */
export const EDITOR_ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

/** `accept` attribute value for editor photo-import file inputs. */
export const EDITOR_IMAGE_ACCEPT = '.jpg,.jpeg,.png';

/** True when the filename has a JPEG/JPG/PNG extension (editor-allowed image). */
export function isEditorImageFile(filename: string): boolean {
  const ext = filename.toLowerCase().split('.').pop();
  return !!ext && EDITOR_ALLOWED_IMAGE_EXTENSIONS.includes(ext);
}

/**
 * Split a selected file list into editor-allowed images (JPEG/JPG/PNG) and the
 * names of rejected files, so callers can warn the user and drop the rest.
 */
export function filterEditorImageFiles(files: File[]): { validFiles: File[]; rejectedNames: string[] } {
  const validFiles: File[] = [];
  const rejectedNames: string[] = [];
  for (const f of files) {
    if (isEditorImageFile(f.name)) {
      validFiles.push(f);
    } else {
      rejectedNames.push(f.name);
    }
  }
  return { validFiles, rejectedNames };
}
